// Pure-math port of iOS `ScanGeometry` (ios/.../Services/ScanGeometry.swift).
// Parses a CapturedRoom room.json into walls / openings / floor polygon / object
// footprints, decomposes walls into solid runs vs door/window gaps, and thickens
// solid runs into mitered double-line plan polygons. Faithful to the Swift logic.

import type {
  CapturedRoomJSON,
  ObjectFootprint,
  OpeningKind,
  OpeningSegment,
  ScanGeometry,
  Surface,
  RoomObject,
  ThickWall,
  Vec2,
  WallDecomposition,
  WallOpeningSlot,
  WallRun,
  WallSegment,
  WallThickness,
} from "./types";
import { DEFAULT_WALL_THICKNESS } from "./types";

// MARK: - Vec2 helpers (mirror SIMD2<Float> ops)

const v = (x: number, y: number): Vec2 => ({ x, y });
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const scale = (a: Vec2, s: number): Vec2 => ({ x: a.x * s, y: a.y * s });
const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
const length = (a: Vec2): number => Math.hypot(a.x, a.y);
const distance = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const vmin = (a: Vec2, b: Vec2): Vec2 => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) });
const vmax = (a: Vec2, b: Vec2): Vec2 => ({ x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) });

// MARK: - Category normalization

/**
 * RoomPlan encodes `category` as a single-key object ({"door":{}}) — normalize to
 * its lowercase token. Tolerates a plain string too.
 */
export function normalizeCategory(category: any): string {
  if (typeof category === "string") return category.toLowerCase();
  if (category && typeof category === "object") {
    const keys = Object.keys(category);
    if (keys.length > 0) return keys[0].toLowerCase();
  }
  return "unknown";
}

/** Maps a normalized object category token to its display label (iOS `label(for:)`). */
function labelForObject(category: any): string {
  switch (normalizeCategory(category)) {
    case "storage": return "Storage";
    case "refrigerator": return "Refrigerator";
    case "stove": return "Stove";
    case "bed": return "Bed";
    case "sink": return "Sink";
    case "washerdryer": return "Washer/Dryer";
    case "toilet": return "Toilet";
    case "bathtub": return "Bathtub";
    case "oven": return "Oven";
    case "dishwasher": return "Dishwasher";
    case "table": return "Table";
    case "sofa": return "Sofa";
    case "chair": return "Chair";
    case "fireplace": return "Fireplace";
    case "television": return "TV";
    case "stairs": return "Stairs";
    default: return "Furniture";
  }
}

// MARK: - Surface math

/**
 * A surface's two endpoints on the floor plane: its center (t[12], t[14]) pushed
 * half its width along its local-X axis projected to the floor, in both directions.
 */
export function surfaceEndpoints(surface: Surface): [Vec2, Vec2] {
  const t = surface.transform;
  const center = v(t[12], t[14]);
  let along = v(t[0], t[2]); // local-X axis projected to floor plane
  const len = length(along);
  along = len > 0 ? scale(along, 1 / len) : v(1, 0);
  const half = surface.dimensions[0] / 2;
  return [sub(center, scale(along, half)), add(center, scale(along, half))];
}

/** The four floor-plane corners of an oriented bounding box, in polygon order. */
function footprintCorners(transform: number[], dimensions: number[]): Vec2[] {
  const t = transform;
  const center = v(t[12], t[14]);
  let xAxis = v(t[0], t[2]);
  let zAxis = v(t[8], t[10]);
  const xLen = length(xAxis);
  const zLen = length(zAxis);
  xAxis = xLen > 0 ? scale(xAxis, 1 / xLen) : v(1, 0);
  zAxis = zLen > 0 ? scale(zAxis, 1 / zLen) : v(0, 1);
  const hx = scale(xAxis, dimensions[0] / 2);
  const hz = scale(zAxis, dimensions[2] / 2);
  // [BL, BR, TR, TL]
  return [
    sub(sub(center, hx), hz),
    sub(add(center, hx), hz),
    add(add(center, hx), hz),
    add(sub(center, hx), hz),
  ];
}

/**
 * Multiply a local polygon corner [x,y,z] by a column-major 16-float transform and
 * return the resulting world (x, z) on the floor plane.
 */
function transformCornerToFloor(transform: number[], corner: number[]): Vec2 {
  const t = transform;
  const [cx, cy, cz] = corner;
  // world = T * [x,y,z,1], column-major.
  const wx = t[0] * cx + t[4] * cy + t[8] * cz + t[12];
  const wz = t[2] * cx + t[6] * cy + t[10] * cz + t[14];
  return v(wx, wz);
}

/** Shoelace signed area (mirrors iOS `shoelaceArea`). */
export function shoelaceArea(polygon: Vec2[]): number {
  if (polygon.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/** Andrew's monotone chain convex hull — fallback room outline. */
export function convexHull(points: Vec2[]): Vec2[] {
  if (points.length < 3) return points.slice();
  const sorted = points.slice().sort((p, q) => (p.x === q.x ? p.y - q.y : p.x - q.x));
  const cross = (o: Vec2, a: Vec2, b: Vec2): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Vec2[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: Vec2[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/** The captured floor with the largest rectangular extent (dimensions.x * dimensions.y). */
function largestFloor(json: CapturedRoomJSON): Surface | undefined {
  let best: Surface | undefined;
  let bestArea = -Infinity;
  for (const f of json.floors ?? []) {
    const area = f.dimensions[0] * f.dimensions[1];
    if (area > bestArea) {
      bestArea = area;
      best = f;
    }
  }
  return best;
}

/** Even-odd ray-cast point-in-polygon on the floor plane. */
export function polygonContains(polygon: Vec2[], point: Vec2): boolean {
  let inside = false;
  let j = polygon.length - 1;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (
      a.y > point.y !== b.y > point.y &&
      point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x
    ) {
      inside = !inside;
    }
    j = i;
  }
  return inside;
}

// MARK: - squareUpRotation

/**
 * Rotation (radians) that squares a footprint to the page: aligns the dominant wall
 * direction to the axes (length-weighted, mod-90°), then adds 90° if needed so the
 * LONGER extent runs top-to-bottom. Returns 0 with no usable walls.
 */
export function squareUpRotation(segments: { start: Vec2; end: Vec2 }[]): number {
  let sin4 = 0;
  let cos4 = 0;
  for (const s of segments) {
    const d = sub(s.end, s.start);
    const len = length(d);
    if (len <= 0.05) continue;
    const a = Math.atan2(d.y, d.x);
    sin4 += len * Math.sin(4 * a); // mod-90° symmetry
    cos4 += len * Math.cos(4 * a);
  }
  if (sin4 === 0 && cos4 === 0) return 0;
  let rot = -0.25 * Math.atan2(sin4, cos4);
  const rotated = (p: Vec2, a: number): Vec2 => {
    const c = Math.cos(a);
    const s = Math.sin(a);
    return v(p.x * c - p.y * s, p.x * s + p.y * c);
  };
  let mn = v(Infinity, Infinity);
  let mx = v(-Infinity, -Infinity);
  for (const s of segments) {
    for (const p of [s.start, s.end]) {
      const r = rotated(p, rot);
      mn = vmin(mn, r);
      mx = vmax(mx, r);
    }
  }
  if (mx.x - mn.x > mx.y - mn.y) rot += Math.PI / 2;
  return rot;
}

// MARK: - Wall decomposition

const SNAP_EPS = 0.05; // 5 cm: joint clustering + jamb collapse
const MIN_OPENING = 0.05;
const MIN_RUN = 0.03;

const wallDirection = (wall: WallSegment): Vec2 => {
  const d = sub(wall.end, wall.start);
  const len = length(d);
  return len > 0 ? scale(d, 1 / len) : v(1, 0);
};
const wallNormal = (wall: WallSegment): Vec2 => {
  const dir = wallDirection(wall);
  return v(-dir.y, dir.x);
};
const wallLength = (wall: WallSegment): number => distance(wall.start, wall.end);
const openingCenter = (o: OpeningSegment): Vec2 => scale(add(o.start, o.end), 0.5);

/**
 * Assigns an opening to a host wall: `parentWallId` match first, else the nearest
 * wall it projects onto within tolerance (perp ≤ 25 cm, projection within [-0.1, L+0.1]).
 */
function hostWallIndex(opening: OpeningSegment, walls: WallSegment[]): number | null {
  if (opening.parentWallId != null) {
    const idx = walls.findIndex((w) => w.surfaceID === opening.parentWallId);
    if (idx >= 0) return idx;
  }
  let best: { idx: number; cost: number } | null = null;
  for (let idx = 0; idx < walls.length; idx++) {
    const wall = walls[idx];
    const center = openingCenter(opening);
    const perp = Math.abs(dot(sub(center, wall.start), wallNormal(wall)));
    const along = dot(sub(center, wall.start), wallDirection(wall));
    const L = wallLength(wall);
    if (!(perp < 0.25 && along > -0.1 && along < L + 0.1)) continue;
    if (best === null || perp < best.cost) best = { idx, cost: perp };
  }
  return best ? best.idx : null;
}

/** Sorts then collapses values within SNAP_EPS so near-coincident breakpoints don't split. */
function collapseSorted(xs: number[]): number[] {
  const out: number[] = [];
  for (const x of xs.slice().sort((a, b) => a - b)) {
    if (out.length === 0 || x - out[out.length - 1] > SNAP_EPS) out.push(x);
  }
  return out;
}

/**
 * Splits each wall into solid runs and opening gaps via 1D interval subtraction.
 * Mirrors iOS `decompose`.
 */
export function decomposeWall(
  walls: WallSegment[],
  openings: OpeningSegment[]
): WallDecomposition[] {
  const byWall: Record<number, OpeningSegment[]> = {};
  for (const opening of openings) {
    const idx = hostWallIndex(opening, walls);
    if (idx === null) continue;
    (byWall[idx] ??= []).push(opening);
  }
  return walls.map((wall, wallIdx) => {
    const a = wall.start;
    const d = wallDirection(wall);
    const L = wallLength(wall);
    // Project host openings to arc-length intervals, clip to the wall, drop noise.
    let raw: { u0: number; u1: number; opening: OpeningSegment }[] = [];
    for (const opening of byWall[wallIdx] ?? []) {
      const uc = dot(sub(openingCenter(opening), a), d);
      const half = opening.width / 2;
      const u0 = Math.max(uc - half, 0);
      const u1 = Math.min(uc + half, L);
      if (u1 - u0 > MIN_OPENING) raw.push({ u0, u1, opening });
    }
    raw.sort((p, q) => p.u0 - q.u0);
    // Merge overlapping / abutting slots.
    const merged: { u0: number; u1: number; opening: OpeningSegment }[] = [];
    for (const slot of raw) {
      const last = merged[merged.length - 1];
      if (last && slot.u0 <= last.u1 + SNAP_EPS) {
        last.u1 = Math.max(last.u1, slot.u1);
      } else {
        merged.push({ ...slot });
      }
    }
    // Solid runs = complement of the merged gaps within [0, L].
    const runs: WallRun[] = [];
    const addRun = (s: number, e: number) => {
      if (e - s <= MIN_RUN) return;
      runs.push({
        id: runs.length,
        s,
        e,
        startPoint: add(a, scale(d, s)),
        endPoint: add(a, scale(d, e)),
      });
    };
    let cursor = 0;
    for (const gap of merged) {
      addRun(cursor, gap.u0);
      cursor = Math.max(cursor, gap.u1);
    }
    addRun(cursor, L);
    const slots: WallOpeningSlot[] = merged.map((gap, i) => ({
      id: i,
      opening: gap.opening,
      u0: gap.u0,
      u1: gap.u1,
      startPoint: add(a, scale(d, gap.u0)),
      endPoint: add(a, scale(d, gap.u1)),
    }));
    const breaks = [0, L];
    for (const gap of merged) {
      breaks.push(gap.u0, gap.u1);
    }
    return {
      id: wall.id,
      wall,
      runs,
      slots,
      breakpoints: collapseSorted(breaks),
    };
  });
}

/**
 * True when a wall hosts a door or cased opening (something you walk THROUGH; a
 * window keeps wall above and below). Mirrors `hasWalkthroughOpening`.
 */
export function hasWalkthroughOpening(decomp: WallDecomposition): boolean {
  return decomp.slots.some(
    (s) => s.opening.kind === "door" || s.opening.kind === "opening"
  );
}

/**
 * Solid wall runs with gaps cut ONLY for doors / cased openings (windows stay solid).
 * Mirrors `walkthroughRuns`. Used by the 3D scene where only full-height gaps exist.
 */
export function walkthroughRuns(decomp: WallDecomposition): { start: Vec2; end: Vec2 }[] {
  const wall = decomp.wall;
  const L = wallLength(wall);
  if (L <= 0) return [];
  const dir = scale(sub(wall.end, wall.start), 1 / L);
  const gaps = decomp.slots
    .filter((s) => s.opening.kind === "door" || s.opening.kind === "opening")
    .map((s) => [Math.max(0, s.u0), Math.min(L, s.u1)] as [number, number])
    .filter(([s, e]) => e > s + 0.02)
    .sort((p, q) => p[0] - q[0]);
  const runs: [number, number][] = [];
  let cursor = 0;
  for (const [g0, g1] of gaps) {
    if (g0 > cursor + 0.04) runs.push([cursor, g0]);
    cursor = Math.max(cursor, g1);
  }
  if (cursor < L - 0.04) runs.push([cursor, L]);
  return runs.map(([s, e]) => ({
    start: add(wall.start, scale(dir, s)),
    end: add(wall.start, scale(dir, e)),
  }));
}

// MARK: - Wall thickening (centerline offset + mitered corners)

const MITER_LIMIT = 4;

/**
 * Turns each solid wall run into a 4-corner plan polygon, mitering at true wall
 * corners (two runs meeting at a shared endpoint) and square-capping at jambs / free
 * ends. Port of iOS `WallThickener.thicken`. Single-room scans: every wall interior.
 */
export function thickenWalls(
  decomps: WallDecomposition[],
  thickness: WallThickness = DEFAULT_WALL_THICKNESS,
  overrides?: Map<string, number>
): ThickWall[] {
  // Per-wall half-thickness: a user override for this wall's surface (construction-grade edit),
  // else the synthesized interior default. Mirrors iOS Room3DSpec (override by surfaceID, else
  // class default — single-room walls are all interior).
  const wallHalf = (w: WallSegment): number =>
    ((w.surfaceID ? overrides?.get(w.surfaceID) : undefined) ?? thickness.interior) / 2;

  interface RunEnd {
    key: string; // `${di}:${ri}`
    isStart: boolean;
    base: Vec2;
    nRun: Vec2; // the run's wall normal (unit)
    half: number;
  }

  // 1. Collect wall-extreme run ends (interior jamb ends get a square cap).
  const ends: RunEnd[] = [];
  decomps.forEach((decomp, di) => {
    const L = wallLength(decomp.wall);
    const n = wallNormal(decomp.wall);
    const half = wallHalf(decomp.wall);
    decomp.runs.forEach((run, ri) => {
      const key = `${di}:${ri}`;
      if (run.s <= SNAP_EPS) {
        ends.push({ key, isStart: true, base: run.startPoint, nRun: n, half });
      }
      if (run.e >= L - SNAP_EPS) {
        ends.push({ key, isStart: false, base: run.endPoint, nRun: n, half });
      }
    });
  });

  // 2. Cluster ends into joints by position.
  const clusters: number[][] = [];
  const used = new Array(ends.length).fill(false);
  for (let i = 0; i < ends.length; i++) {
    if (used[i]) continue;
    const group = [i];
    used[i] = true;
    for (let j = i + 1; j < ends.length; j++) {
      if (used[j]) continue;
      if (distance(ends[i].base, ends[j].base) <= SNAP_EPS) {
        group.push(j);
        used[j] = true;
      }
    }
    clusters.push(group);
  }

  // 3. Per 2-member joint, compute the two miter corners; assign by side of the run normal.
  const startMiter: Record<string, { plus: Vec2; minus: Vec2 }> = {};
  const endMiter: Record<string, { plus: Vec2; minus: Vec2 }> = {};
  for (const group of clusters) {
    if (group.length !== 2) continue;
    const endA = ends[group[0]];
    const endB = ends[group[1]];
    const joint = scale(add(endA.base, endB.base), 0.5);
    const sumN = add(endA.nRun, endB.nRun);
    const lenSum = length(sumN);
    if (lenSum <= 1e-4) continue; // anti-parallel: straight, no corner
    const miterU = scale(sumN, 1 / lenSum);
    const denom = dot(miterU, endA.nRun);
    if (denom <= 1e-3) continue;
    const h = Math.max(endA.half, endB.half);
    const miterLen = h / denom;
    if (miterLen > MITER_LIMIT * h) continue; // too acute → bevel (skip)
    const c1 = add(joint, scale(miterU, miterLen));
    const c2 = sub(joint, scale(miterU, miterLen));
    for (const endIdx of group) {
      const e = ends[endIdx];
      const pair =
        dot(sub(c1, e.base), e.nRun) >= dot(sub(c2, e.base), e.nRun)
          ? { plus: c1, minus: c2 }
          : { plus: c2, minus: c1 };
      if (e.isStart) startMiter[e.key] = pair;
      else endMiter[e.key] = pair;
    }
  }

  // 4. Emit each run's quad, applying miter overrides where present.
  const result: ThickWall[] = [];
  decomps.forEach((decomp, di) => {
    const n = wallNormal(decomp.wall);
    const half = wallHalf(decomp.wall);
    decomp.runs.forEach((run, ri) => {
      const key = `${di}:${ri}`;
      const s =
        startMiter[key] ?? {
          plus: add(run.startPoint, scale(n, half)),
          minus: sub(run.startPoint, scale(n, half)),
        };
      const e =
        endMiter[key] ?? {
          plus: add(run.endPoint, scale(n, half)),
          minus: sub(run.endPoint, scale(n, half)),
        };
      result.push({
        id: key,
        wallID: decomp.wall.id,
        polygon: [s.plus, e.plus, e.minus, s.minus],
      });
    });
  });
  return result;
}

// MARK: - buildScanGeometry

/** Per-scan wall-thickness configuration (from the construction-grade overrides). */
export interface WallThicknessConfig {
  thickness?: WallThickness;
  /** surfaceID → thickness (m) for individually-adjusted walls. */
  thicknessOverrides?: Map<string, number>;
}

/** Build a WallThicknessConfig from a scan's construction-grade override fields (SyncDTOs.ScanPayload).
 *  Mirrors iOS Render3DJobRunner: custom interior/exterior defaults + per-surface overrides, falling
 *  back to the synthesized DEFAULT_WALL_THICKNESS. Structural param so geometry stays lib-decoupled. */
export function wallThicknessFromScan(sp: {
  defaultWallThicknessMeters?: number;
  exteriorWallThicknessMeters?: number;
  wallThicknessOverrideIDs?: string[];
  wallThicknessOverrideValues?: number[];
}): WallThicknessConfig {
  const interior =
    sp.defaultWallThicknessMeters && sp.defaultWallThicknessMeters > 0
      ? sp.defaultWallThicknessMeters
      : DEFAULT_WALL_THICKNESS.interior;
  const exterior =
    sp.exteriorWallThicknessMeters && sp.exteriorWallThicknessMeters > 0
      ? sp.exteriorWallThicknessMeters
      : DEFAULT_WALL_THICKNESS.exterior;
  const ids = sp.wallThicknessOverrideIDs ?? [];
  const vals = sp.wallThicknessOverrideValues ?? [];
  const thicknessOverrides = new Map<string, number>();
  ids.forEach((id, i) => {
    if (i < vals.length && vals[i] > 0) thicknessOverrides.set(id, vals[i]);
  });
  return { thickness: { interior, exterior }, thicknessOverrides };
}

/** Build the derived geometry from a parsed room.json. Port of the iOS init.
 *  `opts` carries the user's construction-grade wall-thickness edits (else synthesized defaults). */
export function buildScanGeometry(json: CapturedRoomJSON, opts?: WallThicknessConfig): ScanGeometry {
  // Walls — skip slivers under 5 cm.
  const walls: WallSegment[] = [];
  (json.walls ?? []).forEach((surface, index) => {
    if (!(surface.dimensions[0] > 0.05)) return;
    const [start, end] = surfaceEndpoints(surface);
    walls.push({
      id: String(index),
      surfaceID: surface.identifier,
      start,
      end,
      height: surface.dimensions[1],
    });
  });

  const openingSegments = (surfaces: Surface[], kind: OpeningKind): OpeningSegment[] =>
    (surfaces ?? []).map((surface, index) => {
      const [start, end] = surfaceEndpoints(surface);
      return {
        id: `${kind}-${index}`,
        kind,
        surfaceID: surface.identifier,
        parentWallId: surface.parentIdentifier ?? null,
        start,
        end,
        width: surface.dimensions[0],
        height: surface.dimensions[1],
        centerY: surface.transform[13],
      };
    });

  const doors = openingSegments(json.doors, "door");
  const windows = openingSegments(json.windows, "window");
  const openings = openingSegments(json.openings, "opening");

  const objects: ObjectFootprint[] = (json.objects ?? []).map(
    (object: RoomObject, index: number) => {
      const corners = footprintCorners(object.transform, object.dimensions);
      return {
        id: String(index),
        label: labelForObject(object.category),
        corners,
        dimensions: object.dimensions,
        center: scale(
          corners.reduce((acc, c) => add(acc, c), v(0, 0)),
          1 / Math.max(corners.length, 1)
        ),
        isStairs: normalizeCategory(object.category) === "stairs",
        transform: object.transform,
      };
    }
  );

  // Floor outline: prefer the largest captured floor's polygon (corners are LOCAL —
  // lift them through the transform), fall back to convex hull of wall endpoints.
  let polygon: Vec2[] = [];
  const floor = largestFloor(json);
  if (floor) {
    polygon = floor.polygonCorners.map((corner) =>
      transformCornerToFloor(floor.transform, corner)
    );
  }
  if (polygon.length < 3) {
    const endpoints: Vec2[] = [];
    for (const w of walls) endpoints.push(w.start, w.end);
    polygon = convexHull(endpoints);
  }

  const floorAreaSquareMeters = Math.abs(shoelaceArea(polygon));
  const perimeterMeters = walls.reduce((acc, w) => acc + wallLength(w), 0);
  const heights = walls.map((w) => w.height).sort((a, b) => a - b);
  const ceilingHeightMeters = heights.length === 0 ? 0 : heights[Math.floor(heights.length / 2)];

  // Bounds over walls + object corners + floor polygon.
  const points: Vec2[] = [];
  for (const w of walls) points.push(w.start, w.end);
  for (const o of objects) points.push(...o.corners);
  points.push(...polygon);
  let boundsMin: Vec2;
  let boundsMax: Vec2;
  if (points.length === 0) {
    boundsMin = v(-1, -1);
    boundsMax = v(1, 1);
  } else {
    boundsMin = points.reduce((acc, p) => vmin(acc, p), points[0]);
    boundsMax = points.reduce((acc, p) => vmax(acc, p), points[0]);
  }

  // Floor datum.
  let floorY: number;
  if (floor) {
    floorY = floor.transform[13];
  } else {
    const bottoms = (json.walls ?? []).map((w) => w.transform[13] - w.dimensions[1] / 2);
    floorY = bottoms.length ? Math.min(...bottoms) : 0;
  }

  const wallDecompositions = decomposeWall(walls, [...doors, ...windows, ...openings]);
  const thickWalls = thickenWalls(wallDecompositions, opts?.thickness, opts?.thicknessOverrides);

  return {
    walls,
    doors,
    windows,
    openings,
    objects,
    floorPolygon: polygon,
    boundsMin,
    boundsMax,
    floorAreaSquareMeters,
    ceilingHeightMeters,
    perimeterMeters,
    floorY,
    wallDecompositions,
    thickWalls,
    raw: json,
  };
}
